import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402/api';

const getAxiosInstance = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
};

export type MassActionType =
  | 'BAN_USERS'
  | 'UNBAN_USERS'
  | 'SUSPEND_USERS'
  | 'DELETE_POSTS'
  | 'HIDE_POSTS'
  | 'RESTORE_POSTS'
  | 'REFUND_PAYMENTS'
  | 'DELETE_COMMENTS'
  | 'SEND_NOTIFICATION'
  | 'EXPORT_DATA';

export type MassActionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface MassAction {
  id: string;
  adminId: string;
  actionType: MassActionType;
  status: MassActionStatus;
  targetType: string;
  targetIds: string[];
  reason: string;
  totalTargets: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  errorDetails?: any;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  admin?: {
    username: string;
    email: string;
  };
}

export const massActionsService = {
  /**
   * Create a new mass action
   */
  create: async (payload: {
    actionType: MassActionType;
    targetIds: string[];
    reason: string;
    notificationMessage?: string;
  }): Promise<{ massAction: MassAction }> => {
    const api = getAxiosInstance();
    const { data } = await api.post('/admin/mass-actions', payload);
    return data;
  },

  /**
   * Get a single mass action by ID
   */
  get: async (id: string): Promise<{ massAction: MassAction }> => {
    const api = getAxiosInstance();
    const { data } = await api.get(`/admin/mass-actions/${id}`);
    return data;
  },

  /**
   * List all mass actions with pagination and filters
   */
  list: async (params?: {
    page?: number;
    limit?: number;
    actionType?: MassActionType;
    status?: MassActionStatus;
  }): Promise<{
    actions: MassAction[];
    pagination: {
      page: number;
      limit: number;
      totalPages: number;
      totalCount: number;
    };
  }> => {
    const api = getAxiosInstance();
    const { data } = await api.get('/admin/mass-actions', { params });
    return data;
  },

  /**
   * Cancel a pending/processing mass action
   */
  cancel: async (id: string): Promise<{ massAction: MassAction; message: string }> => {
    const api = getAxiosInstance();
    const { data} = await api.put(`/admin/mass-actions/${id}/cancel`);
    return data;
  },
};
